// src/pages/admin/ListingCabs.js
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from "react-router-dom";
import axios from 'axios';
import SEO from '../../../components/common/SEO';
import confirmDeletion from '../../utils/confirmDeletion';

import { formatDateTime } from '../../../utils/dateUtils';
function ListingCabs() {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    const location = useLocation();

    const [vendor, setVendor] = useState(null);

    // API data
    const [cabs, setCabs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [apiError, setApiError] = useState(null);

    const [successMessage, setSuccessMessage] = useState('');
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'desc' });

    const pageSize = 20;

    useEffect(() => {
        const storedUserInfo = localStorage.getItem('userInfo');
        if (storedUserInfo) {
            setVendor(JSON.parse(storedUserInfo));
        }

        const fetchCabs = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem('authToken');
                const res = await axios.get(`${API_BASE_URL}/cabs`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                // Expecting an array response (controller returns res.json(cabs))
                const data = Array.isArray(res.data) ? res.data : (res.data.data || res.data);
                setCabs(data || []);
                setApiError(null);
            } catch (err) {
                console.error('Error fetching cabs:', err);
                setApiError('Failed to fetch cabs. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchCabs();
    }, [API_BASE_URL]);

    useEffect(() => {
        if (location.state && location.state.message) {
            setSuccessMessage(location.state.message);
            const timer = setTimeout(() => {
                setSuccessMessage('');
                window.history.replaceState({}, document.title);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [location.state]);

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        setCurrentPage(1);
    };

    const getNestedValue = (obj, path) => {
        return path.split('.').reduce((acc, part) => acc && acc[part], obj);
    };

    // Sorting
    const sortedData = [...cabs].sort((a, b) => {
        if (!sortConfig.key) return 0;
        const aValue = getNestedValue(a, sortConfig.key) ?? '';
        const bValue = getNestedValue(b, sortConfig.key) ?? '';

        // try numeric compare
        if (!isNaN(Number(aValue)) && !isNaN(Number(bValue))) {
            return sortConfig.direction === 'asc' ? Number(aValue) - Number(bValue) : Number(bValue) - Number(aValue);
        }

        return sortConfig.direction === 'asc'
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
    });

    // Filter (search across useful fields)
    const filteredData = sortedData.filter(cab => {
        if (!search) return true;
        const q = search.toLowerCase();
        const checks = [
            cab.brand_name,
            cab.cab_city,
            cab.cab_contact_person,
            cab.cab_contact_number,
            cab.cab_description,
            cab.package_price && String(cab.package_price)
        ];
        return checks.some(v => v && String(v).toLowerCase().includes(q));
    });

    const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));
    const paginatedData = filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
    };

    const renderSortArrow = (key) => {
        if (sortConfig.key !== key) return '';
        return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
    };

    const renderStatusBadge = (status, reason) => {
        let badgeClass = 'bg-secondary';
        if (status === 'Published') badgeClass = 'bg-success';
        else if (status === 'Under Review') badgeClass = 'bg-warning';
        else if (status === 'Rejected') badgeClass = 'bg-danger';

        return (
            <div>
                <span className={`badge ${badgeClass}`}>{status || 'Published'}</span>
                {status === 'Rejected' && reason && (
                    <div className="text-danger mt-1" style={{ fontSize: '11px', whiteSpace: 'normal', maxWidth: '150px' }}>
                        <i className="ri-error-warning-line align-middle me-1"></i>
                        {reason}
                    </div>
                )}
            </div>
        );
    };

    // Delete cab (with confirmation)
    const handleDelete = async (cabId) => {
        if (!(await confirmDeletion())) return;
        try {
            const token = localStorage.getItem('authToken');
            await axios.delete(`${API_BASE_URL}/cabs/${cabId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCabs(prev => prev.filter(c => c.id !== cabId));
            setSuccessMessage('Cab deleted successfully!');
            setTimeout(() => setSuccessMessage(''), 5000);
        } catch (error) {
            console.error('Error deleting cab:', error);
            alert('Failed to delete cab.');
        }
    };

    return (
        <div className="row">
            <SEO title="Cabs Management | Mangoloops" />
            {/* <div className="col-12">
                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                    <h4 className="mb-sm-0">Cabs</h4>
                    <div className="page-title-right">
                        <ol className="breadcrumb m-0">
                            <li className="breadcrumb-item"><a href="#">Dashboard</a></li>
                            <li className="breadcrumb-item active">Cabs</li>
                        </ol>
                    </div>
                </div>
            </div> */}

            <div className="col-lg-12">
                <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <h4 className="card-title mb-0">Cabs Listing</h4>
                        <div className="d-flex flex-wrap gap-2">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="🔍 Search Cabs..."
                                value={search}
                                onChange={handleSearchChange}
                                style={{ width: '300px' }}
                            />
                            <Link to="/admin/add-cab" className="btn btn-soft-secondary">Add Cab</Link>
                        </div>
                    </div>

                    {successMessage && <div className="alert alert-success m-3">{successMessage}</div>}
                    {apiError && <div className="alert alert-danger m-3">{apiError}</div>}

                    <div className="card-body">
                        <div className="table-responsive">
                            <table className="table table-bordered table-hover">
                                <thead className="table-light">
                                    <tr>
                                        <th style={{ width: "60px" }}>Sr. No.</th>
                                        <th style={{ width: "100px" }}>Image</th>
                                        <th onClick={() => requestSort('brand_name')} style={{ cursor: 'pointer' }}>Brand Name{renderSortArrow('brand_name')}</th>
                                        <th onClick={() => requestSort('package_price')} style={{ cursor: 'pointer' }}>Package Price{renderSortArrow('package_price')}</th>
                                        <th onClick={() => requestSort('offer_price')} style={{ cursor: 'pointer' }}>Offer Price{renderSortArrow('offer_price')}</th>
                                        <th onClick={() => requestSort('cab_city')} style={{ cursor: 'pointer' }}>City{renderSortArrow('cab_city')}</th>
                                        <th onClick={() => requestSort('status')} style={{ cursor: 'pointer' }}>Status{renderSortArrow('status')}</th>
                                        <th onClick={() => requestSort('created_at')} style={{ cursor: 'pointer' }}>Created At{renderSortArrow('created_at')}</th>
                                        <th style={{ width: "120px" }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan="8" className="text-center">Loading...</td></tr>
                                    ) : paginatedData.length > 0 ? (
                                        paginatedData.map((cab, index) => (
                                            <tr key={cab.id}>
                                                <td>{(currentPage - 1) * pageSize + index + 1}</td>
                                                <td>
                                                    {cab.Images && cab.Images.length > 0 ? (
                                                        <img
                                                            src={cab.Images[0].image_url}
                                                            alt={cab.brand_name || `cab-${cab.id}`}
                                                            style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
                                                            onError={(e) => { e.target.onerror = null; e.target.src = '/placeholder-60.png'; }}
                                                        />
                                                    ) : (
                                                        <span className="text-muted">No Image</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <Link to={`/admin/edit-cab/${cab.id}`}>
                                                        {cab.brand_name || `Cab #${cab.id}`}
                                                    </Link>
                                                </td>
                                                <td>₹{cab.package_price ?? 'N/A'}</td>
                                                <td>₹{cab.offer_price ?? 'N/A'}</td>
                                                <td>{cab.cab_city || 'N/A'}</td>
                                                <td>
                                                    {renderStatusBadge(cab.status, cab.rejection_reason)}
                                                </td>
                                                <td>{cab.created_at ? formatDateTime(cab.created_at) : 'N/A'}</td>
                                                <td>
                                                    <div className="d-flex gap-2">
                                                        {/* <Link to={`/admin/edit-cab/${cab.id}`} className="btn btn-sm btn-outline-primary" title="Edit">
                                                            <i className="ri-edit-2-line"></i>
                                                        </Link> */}
                                                        <button onClick={() => handleDelete(cab.id)} className="btn btn-sm btn-outline-danger" title="Delete">
                                                            <i className="ri-delete-bin-line"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr><td colSpan="8" className="text-center">No cabs found.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap">
                            <div className="mb-2">
                                {filteredData.length > 0 && (
                                    <span>
                                        Showing {(currentPage - 1) * pageSize + 1} to{" "}
                                        {Math.min(currentPage * pageSize, filteredData.length)} of {filteredData.length} results
                                    </span>
                                )}
                            </div>
                            <div>
                                <ul className="pagination pagination-sm mb-0">
                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                        <button className="page-link" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}>Previous</button>
                                    </li>
                                    {Array.from({ length: totalPages }, (_, i) => (
                                        <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                                            <button className="page-link" onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                                        </li>
                                    ))}
                                    <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                                        <button className="page-link" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}>Next</button>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ListingCabs;
